import type { ComponentType, PropsWithChildren } from "react";

import Svg, {
  Defs,
  LinearGradient,
  RadialGradient,
  Rect,
  Stop,
} from "react-native-svg";

import Box from "@/ui/layout/Box";
import { ColorToken, ColorTokenValues } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

type RadialGradientBackgroundVariant = "standard" | "subtle";

interface RadialGradientBackgroundProps extends PropsWithChildren<ViewWithTestID> {
  variant?: RadialGradientBackgroundVariant;
}

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- react-native-svg web types omit children from Defs
const GradientDefs = Defs as ComponentType<PropsWithChildren>;

const AccentBlue = ColorTokenValues[ColorToken.AccentBlue];
const Primary = ColorTokenValues[ColorToken.Primary100];
const Periwinkle = ColorTokenValues[ColorToken.AccentPeriwinkle];

interface GradientConfig {
  baseEnd: string;
  baseStart: string;
  opacity: [number, number, number, number, number];
}

// The colors used in the gradient are not part of the ColorTokenValues because they are specific to the gradient design
// to ensure that the gradient has the desired visual effect.
const VariantConfig: Record<RadialGradientBackgroundVariant, GradientConfig> = {
  standard: {
    baseEnd: "#F1F7FA",
    baseStart: "#FBFCFE",
    opacity: [0.16, 0.2, 0.18, 0.14, 0.1],
  },
  subtle: {
    baseEnd: "#F5F9FB",
    baseStart: "#FCFDFE",
    opacity: [0.08, 0.1, 0.09, 0.07, 0.05],
  },
};

const GradientBackground = ({
  variant = "standard",
}: {
  variant?: RadialGradientBackgroundVariant;
}) => {
  const config = VariantConfig[variant];

  return (
    <Svg height="100%" width="100%">
      <GradientDefs>
        <LinearGradient id="base" x1="37%" x2="63%" y1="2%" y2="98%">
          <Stop offset="0" stopColor={config.baseStart} />
          <Stop offset="1" stopColor={config.baseEnd} />
        </LinearGradient>

        <RadialGradient
          cx="85%"
          cy="2%"
          gradientUnits="userSpaceOnUse"
          id="g1"
          rx="66%"
          ry="42%"
        >
          <Stop
            offset="0"
            stopColor={AccentBlue}
            stopOpacity={config.opacity[0]}
          />
          <Stop offset="0.56" stopColor={AccentBlue} stopOpacity="0" />
        </RadialGradient>

        <RadialGradient
          cx="3%"
          cy="18%"
          gradientUnits="userSpaceOnUse"
          id="g2"
          rx="64%"
          ry="44%"
        >
          <Stop
            offset="0"
            stopColor={Primary}
            stopOpacity={config.opacity[1]}
          />
          <Stop offset="0.56" stopColor={Primary} stopOpacity="0" />
        </RadialGradient>

        <RadialGradient
          cx="99%"
          cy="52%"
          gradientUnits="userSpaceOnUse"
          id="g3"
          rx="72%"
          ry="48%"
        >
          <Stop
            offset="0"
            stopColor={Periwinkle}
            stopOpacity={config.opacity[2]}
          />
          <Stop offset="0.54" stopColor={Periwinkle} stopOpacity="0" />
        </RadialGradient>

        <RadialGradient
          cx="12%"
          cy="95%"
          gradientUnits="userSpaceOnUse"
          id="g4"
          rx="60%"
          ry="44%"
        >
          <Stop
            offset="0"
            stopColor={Primary}
            stopOpacity={config.opacity[3]}
          />
          <Stop offset="0.56" stopColor={Primary} stopOpacity="0" />
        </RadialGradient>

        <RadialGradient
          cx="62%"
          cy="80%"
          gradientUnits="userSpaceOnUse"
          id="g5"
          rx="58%"
          ry="50%"
        >
          <Stop
            offset="0"
            stopColor={AccentBlue}
            stopOpacity={config.opacity[4]}
          />
          <Stop offset="0.60" stopColor={AccentBlue} stopOpacity="0" />
        </RadialGradient>
      </GradientDefs>

      <Rect fill="url(#base)" height="100%" width="100%" />
      <Rect fill="url(#g1)" height="100%" width="100%" />
      <Rect fill="url(#g2)" height="100%" width="100%" />
      <Rect fill="url(#g3)" height="100%" width="100%" />
      <Rect fill="url(#g4)" height="100%" width="100%" />
      <Rect fill="url(#g5)" height="100%" width="100%" />
    </Svg>
  );
};

const RadialGradientBackground = ({
  children,
  testID,
  variant = "standard",
}: RadialGradientBackgroundProps) => {
  return (
    <Box
      flex={1}
      position="relative"
      testID={joinTestIDs(testID, "radial-gradient-background")}
    >
      <Box
        bottom={0}
        left={0}
        position="absolute"
        right={0}
        top={0}
        zIndex={-1}
      >
        <GradientBackground variant={variant} />
      </Box>
      {children}
    </Box>
  );
};

export type { RadialGradientBackgroundProps };
export default RadialGradientBackground;
